import { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { getPopupPositionDataset, POPUP_DELAY_STORAGE_KEY } from '@constants/widget';
import { useThrottleValue } from '@hooks';

const PreviewContainer = styled('div')({
    width: '100%',
    height: '100%',
    minHeight: '400px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: '#F2F2F2',
    '@media (max-width: 420px)': {
        minHeight: '300px',
    },
});

const PreviewFrame = styled('iframe')({
    width: '100%',
    height: '100%',
    border: 'none',
});

const GRID_SCALING = `
            box-sizing: border-box;
            width: calc(100% / 0.33);
            transform: scale(0.33);
            transform-origin: 0 0;`;

const WidgetPreview = ({ widgetKey, type, formValues = {} }) => {
    const offsetX = useThrottleValue(formValues.offsetX);
    const offsetY = useThrottleValue(formValues.offsetY);

    const htmlContent = useMemo(() => {
        const {
            locations = [],
            brands = [],
            integrations = [],
            onlyFeatured = false,
            count = '24',
            sorting = '0',
            backgroundColor = '#F1F1F1',
            alignment,
        } = formValues;

        const locationIds = locations
            .map(item => item?.id || item)
            .filter(Boolean)
            .join(',');
        const brandsJson = JSON.stringify(brands || []);
        const channelIds = integrations.join(',');
        const bgColor = type === 'popup' ? '#F2F2F2' : backgroundColor;
        const scaling = type === 'grid' ? GRID_SCALING : '';

        const isDevelopment = import.meta.env.MODE === 'development';
        const widgetBaseUrl = isDevelopment ? 'https://sandbox.pluspoint.io' : window.location.origin;
        const widgetUrl = `${widgetBaseUrl}/widget/${
            type === 'popup' ? 'pl.widget.single.min.js' : 'pl.widget.grid.min.js'
        }`;
        const scriptId = type === 'popup' ? 'pl-single-widget' : 'pl-grid-widget';

        return `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <style>
        body {
            background-color: ${bgColor};
            margin: 0;
            padding: 20px;
            min-height: 100vh;
            overflow: hidden;${scaling}
        }
    </style>
</head>
<body>
    ${type === 'grid' ? '<div id="pl-grid-widget-container"></div>' : ''}
    <script>
        ${
            type === 'popup'
                ? `
        try {
            localStorage.removeItem('${POPUP_DELAY_STORAGE_KEY}');
        } catch(e) {}
        `
                : ''
        }

        var script = document.createElement("script");
        script.id = "${scriptId}";
        script.dataset.key = "${widgetKey}";
        script.dataset.companies = "[${locationIds}]";
        script.dataset.brands = '${brandsJson}';
        script.dataset.channels = "[${channelIds}]";
        script.dataset.onlyfeatured = "${onlyFeatured}";
        script.dataset.count = "${count}";
        script.dataset.sorting = "${sorting}";
        script.dataset.preview = "true";
        ${
            type === 'popup'
                ? getPopupPositionDataset('script', { alignment, offsetX, offsetY })
                      .map(assignment => `${assignment};`)
                      .join('\n        ')
                : `script.dataset.background = "${backgroundColor}";`
        }
        script.src = "${widgetUrl}";
        document.body.appendChild(script);
    </script>
</body>
</html>`;
    }, [widgetKey, type, formValues, offsetX, offsetY]);

    return (
        <PreviewContainer>
            <PreviewFrame
                key={htmlContent}
                srcDoc={htmlContent}
                title="Widget Preview"
                sandbox="allow-scripts allow-same-origin"
            />
        </PreviewContainer>
    );
};

export default WidgetPreview;
